import * as React from 'react'

import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { HiddenField } from 'dash/shared'
import { ChannelIcon } from 'dash/shared/atoms/ChannelIcon'
import {
  Edit,
  FormDataConsumer,
  SaveButton,
  SelectInput as RASelectInput,
  SimpleForm,
  Toolbar,
  useNotify,
  useRedirect
} from 'react-admin'
import { useFormContext } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import AppState from 'shared/app_state'
import { TwoColumnInput } from 'shared/molecules/TwoColumnInput'
import { ConfigEditInputs } from './config_edit_inputs'
import { channelLookup } from './types'

const CustomSaveButton = (props) => {
  const navigate = useNavigate()
  const notify = useNotify()
  const redirect = useRedirect()

  // redirect back to Form.Notifications tab if originally
  // navigated to Channel.Create from there
  // https://stackoverflow.com/a/62341406

  const onSuccess = (response) => {
    notify(`Channel Edited!`, { type: 'success' })

    const lastPage = AppState.getLastPage()
    if (!!lastPage && lastPage.includes('/services/form/')) {
      // no way to access the last viewed page from the history object
      // b/c react-admin is using createHashHistory https://github.com/remix-run/history/blob/v4/docs/GettingStarted.md
      // Use AppState.lastPage value here instead
      AppState.resetLastPage()
      navigate(-1)
    } else redirect(`/account/${AppState.getCurrentAccount().id}/channel`)
  }

  return (
    <div style={{ marginTop: 15 }}>
      <SaveButton
        icon={false}
        {...props}
        mutationOptions={{ onSuccess: onSuccess }}
        type="button"
        label="Update Channel"
        submitOnEnter={true}
      />
    </div>
  )
}

const ChannelEditToolbar = (props) => <Toolbar {...props}></Toolbar>

const Container = styled('div')({
  display: 'flex',
  flexFlow: 'row nowrap',
  alignItems: 'center',
})

export const ChannelEdit = () => {
  const accountId = AppState.getCurrentAccount()?.id
  return (
    <Edit>
      <SimpleForm toolbar={false}>
        <Typography
          style={{
            width: '75%',
            marginBottom: 8,
          }}
          variant="subtitle1"
          color="textSecondary"
          component="p"
        >
          A channel is a connection to an external service that you can use to create integrated workflows for your
          data. Create channels to automate notifications and data streams.
        </Typography>
        {/* <SelectInput /> */}

        <TwoColumnInput title="Channel Type" description="External service to connect to">
          <SelectInput />
        </TwoColumnInput>
        {/* <TwoColumnInput title="Options" description="">
        </TwoColumnInput>
        <TwoColumnInput title="Webhook URL" description="Generated in your Slack channel settings.">
          <TextInput fullWidth source="webhookUrl" label="Webhook" />
        </TwoColumnInput>
        <TwoColumnInput title="Channel Name (optional)" description="Input the channel name you'd like this notification to go to.">
          <TextInput fullWidth source="channel" label="Channel" />
        </TwoColumnInput> */}
        {/* <Typography
          style={{
            width: '100%',
            marginBottom: 8,
          }}
          variant="subtitle1"
          color="textSecondary"
          component="p"
        >
          Once created, this channel can receive automatic notifications when events occur in PowCloud.
        </Typography> */}

        <FormDataConsumer>
          {({ formData, ...rest }) => {
            return (
              formData.type && (
                <fieldset style={{ width: '100%' }}>
                  <Typography
                    variant="h6"
                    component="legend"
                    style={{
                      color: '#767676',
                    }}
                  >
                    Options
                  </Typography>
                  <ConfigEditInputs disabled={false} {...rest} showTitle={false} fullWidth formData={formData} />
                </fieldset>
              )
            )
          }}
        </FormDataConsumer>
        <HiddenField source="accountId" value={accountId} />

        <CustomSaveButton />
      </SimpleForm>
    </Edit>
  )
}

const SelectInput = () => {
  const { watch, unregister } = useFormContext()
  const selectedChannelType = watch('type')

  const renderValue = (value) => {
    const id = typeof value === 'string' ? value : value?.id
    const channelType = channelLookup[id]

    if (!channelType) {
      throw new Error('No valid ChannelType found when trying to render list item' + id)
    }

    return (
      <Container>
        <ChannelIcon channelType={channelType} />
        {channelType.Name}
      </Container>
    )
  }

  React.useEffect(() => {
    // Unregister all fields when notification channel type is changed.
    unregister('config')
  }, [selectedChannelType])

  return (
    <RASelectInput
      fullWidth
      disabled
      source="type"
      choices={[
        { id: 'Channels::SlackChannel', name: 'Slack' },
        { id: 'Channels::EmailChannel', name: 'Email' },
        { id: 'Channels::MailjetChannel', name: 'Mailjet' },
      ]}
      optionText={(value) => renderValue(value)}
      translateChoice={false}
      inputProps={{ renderValue }}
    />
  )
}
